import { de, en, type MessageKey } from "./catalog";
import { FALLBACK_LOCALE, type Locale } from "./locales";

const CATALOGUES: Record<Locale, Record<MessageKey, string>> = {
  "de-DE": de,
  "en-GB": en,
};

/** Values interpolated into `{name}` placeholders. */
export type Params = Record<string, string | number>;

/**
 * Renders one message in one language.
 *
 * A placeholder with no value is left standing rather than replaced with
 * "undefined": a visible `{allowed}` is a bug report, and the word "undefined"
 * in the middle of a German sentence is a mystery.
 */
export function translate(locale: Locale, key: MessageKey, params?: Params): string {
  const catalogue = CATALOGUES[locale] ?? CATALOGUES[FALLBACK_LOCALE];
  const template = catalogue[key];

  // The type system makes this unreachable for a literal key. It is reachable
  // for a key *computed* at runtime — a field name from the API, say — and
  // showing the key beats throwing inside a render.
  if (template === undefined) return String(key);

  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/** The shape a component gets: `t("nav.accounts")`. */
export type Translate = (key: MessageKey, params?: Params) => string;

export function translatorFor(locale: Locale): Translate {
  return (key, params) => translate(locale, key, params);
}
